import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";

/**
 * ImageModal - A reusable component for displaying images in a modal with slider functionality
 *
 * @param {Object} props
 * @param {string|Array<string>} props.images - Single image or array of images (base64 or URL)
 * @param {number} props.initialIndex - Initial image index to display (default: 0)
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {string} props.alt - Alt text for images (default: "Image")
 * @param {boolean} props.isBase64 - Whether images are base64 encoded (default: true)
 */
const ImageModal = ({
  images,
  initialIndex = 0,
  onClose,
  isOpen,
  alt = "Image",
  isBase64 = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Normalize images to always be an array
  const imageArray = Array.isArray(images) ? images : [images];
  const hasMultipleImages = imageArray.length > 1;

  // Update current index when initialIndex changes
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleNext = () => {
    if (hasMultipleImages) {
      setCurrentIndex((prev) => (prev + 1) % imageArray.length);
    }
  };

  const handlePrev = () => {
    if (hasMultipleImages) {
      setCurrentIndex(
        (prev) => (prev - 1 + imageArray.length) % imageArray.length
      );
    }
  };

  const handleBackdropClick = (e) => {
    // Close modal only if clicking on backdrop, not the image
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !imageArray[currentIndex]) {
    return null;
  }

  const currentImage = imageArray[currentIndex];
  const imageSrc = isBase64
    ? `data:image/jpeg;base64,${currentImage}`
    : currentImage;

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-90 backdrop-blur-sm transition-opacity duration-300 ease-in-out p-2 sm:p-4"
      style={{ zIndex: 9999 }}
      onClick={handleBackdropClick}
    >
      {/* Modal Content */}
      <div className="relative w-full max-w-[95vw] max-h-[95vh] flex flex-col items-center justify-center">
        {/* Close Button - repositioned for mobile */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-0 sm:right-0 md:-top-12 md:-right-12 lg:-top-14 lg:-right-14 bg-red-500 hover:bg-red-600 text-white rounded-full w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl z-[10000] focus:outline-none focus:ring-2 focus:ring-red-400 group"
          aria-label="Close modal"
        >
          <FaTimes className="text-base sm:text-lg md:text-xl group-hover:rotate-90 transition-transform duration-300" />
        </button>

        {/* Previous Button - repositioned for mobile */}
        {hasMultipleImages && (
          <button
            onClick={handlePrev}
            className="absolute left-2 sm:left-4 md:-left-16 lg:-left-20 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl z-[10000] focus:outline-none focus:ring-2 focus:ring-blue-400 group"
            aria-label="Previous image"
          >
            <FaChevronLeft className="text-base sm:text-lg md:text-xl group-hover:-translate-x-1 transition-transform duration-200" />
          </button>
        )}

        {/* Image */}
        <div className="relative flex items-center justify-center w-full px-10 sm:px-12 md:px-0">
          <img
            src={imageSrc}
            alt={`${alt} ${
              hasMultipleImages
                ? `${currentIndex + 1} of ${imageArray.length}`
                : ""
            }`}
            className="max-w-full max-h-[75vh] sm:max-h-[80vh] md:max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl border-2 sm:border-4 border-blue-500 transition-all duration-300"
          />

          {/* Image Counter */}
          {hasMultipleImages && (
            <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-70 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold backdrop-blur-sm">
              {currentIndex + 1} / {imageArray.length}
            </div>
          )}
        </div>

        {/* Next Button - repositioned for mobile */}
        {hasMultipleImages && (
          <button
            onClick={handleNext}
            className="absolute right-2 sm:right-4 md:-right-16 lg:-right-20 top-1/2 -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white rounded-full w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl z-[10000] focus:outline-none focus:ring-2 focus:ring-blue-400 group"
            aria-label="Next image"
          >
            <FaChevronRight className="text-base sm:text-lg md:text-xl group-hover:translate-x-1 transition-transform duration-200" />
          </button>
        )}
      </div>

      {/* Navigation Hint */}
      {hasMultipleImages && (
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 text-white text-xs sm:text-sm bg-black bg-opacity-50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg backdrop-blur-sm hidden md:block">
          Use ← → arrow keys to navigate
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ImageModal;
